// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { SchedulingConfigurationId } from './SchedulingConfigurationId';
import { PJDistributionPlanConfiguration } from './PJDistributionPlanConfiguration';
import { SalesCategoryGroup } from './SalesCategoryGroup';
import { DummySalesmanProperties } from './DummySalesmanProperties';

import type { SchedulingConfigurationIdModelType } from './SchedulingConfigurationId';
import type { PJDistributionPlanConfigurationModelType } from './PJDistributionPlanConfiguration';
import type { SalesCategoryGroupModelType } from './SalesCategoryGroup';
import type { DummySalesmanPropertiesModelType } from './DummySalesmanProperties';

type TransportModeEnumType = 'WALKING' | 'VEHICLE';

export type PjPlanConfigurationModelType = {
    schedulingConfigId: SchedulingConfigurationIdModelType,
    transportMode: TransportModeEnumType,
    capacity?: number,
    capacities: Object,
    needsRevenueFairnessAcrossDays: boolean,
    autoAssign: boolean,
    serializedSchedulingConfig: string,
    serializedVaeConfig: string,
    needsExistingBeatPlan: boolean,
    distributionPlanConfiguration: PJDistributionPlanConfigurationModelType,
    isExtraOutletAllowedInFrozenBeats: boolean,
    createDummies: boolean,
    enableMultiTrip: boolean,
    salesmanCategoryGroups: ?{
        [key: string]: SalesCategoryGroupModelType
    },
    salesmanCategoryGroupCapacities: ?{
        [key: string]: string
    },
    dummySalesmanProperties: ?{
        [key: string]: DummySalesmanPropertiesModelType
    }
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PjPlanConfiguration extends ImmutableModel {
    static fromJS(json: PjPlanConfigurationModelType): PjPlanConfiguration {
        const state: Object = Object.assign({}, json);
        state.schedulingConfigId = SchedulingConfigurationId.fromJS(
            state.schedulingConfigId
        );
        state.distributionPlanConfiguration = PJDistributionPlanConfiguration.fromJS(
            state.distributionPlanConfiguration
        );
        state.salesmanCategoryGroups = state.salesmanCategoryGroups
            ? Immutable.Map(state.salesmanCategoryGroups).map(item =>
                  SalesCategoryGroup.fromJS(item)
              )
            : state.salesmanCategoryGroups;
        state.salesmanCategoryGroupCapacities = state.salesmanCategoryGroupCapacities
            ? Immutable.Map(state.salesmanCategoryGroupCapacities)
            : state.salesmanCategoryGroupCapacities;
        state.dummySalesmanProperties = state.dummySalesmanProperties
            ? Immutable.Map(state.dummySalesmanProperties).map(item =>
                  DummySalesmanProperties.fromJS(item)
              )
            : state.dummySalesmanProperties;
        return new this(Immutable.Map(state));
    }

    toJS(): PjPlanConfigurationModelType {
        const js: PjPlanConfigurationModelType = {
            schedulingConfigId: this.schedulingConfigId.toJS(),
            transportMode: this.transportMode,
            capacities: this.capacities,
            needsRevenueFairnessAcrossDays: this.needsRevenueFairnessAcrossDays,
            autoAssign: this.autoAssign,
            serializedSchedulingConfig: this.serializedSchedulingConfig,
            serializedVaeConfig: this.serializedVaeConfig,
            needsExistingBeatPlan: this.needsExistingBeatPlan,
            distributionPlanConfiguration: this.distributionPlanConfiguration.toJS(),
            isExtraOutletAllowedInFrozenBeats: this
                .isExtraOutletAllowedInFrozenBeats,
            createDummies: this.createDummies,
            enableMultiTrip: this.enableMultiTrip,
            salesmanCategoryGroups: this.salesmanCategoryGroups
                ? this.salesmanCategoryGroups
                      .map(item => item.toJS())
                      .toObject()
                : this.salesmanCategoryGroups,
            salesmanCategoryGroupCapacities: this
                .salesmanCategoryGroupCapacities
                ? this.salesmanCategoryGroupCapacities.toObject()
                : this.salesmanCategoryGroupCapacities,
            dummySalesmanProperties: this.dummySalesmanProperties
                ? this.dummySalesmanProperties
                      .map(item => item.toJS())
                      .toObject()
                : this.dummySalesmanProperties
        };

        if (this.capacity != null) {
            js.capacity = this.capacity;
        }

        return js;
    }

    get schedulingConfigId(): SchedulingConfigurationId {
        return this._state.get('schedulingConfigId');
    }

    setSchedulingConfigId(schedulingConfigId: SchedulingConfigurationId): this {
        return this.clone(
            this._state.set('schedulingConfigId', schedulingConfigId)
        );
    }

    get transportMode(): TransportModeEnumType {
        return this._state.get('transportMode');
    }

    setTransportMode(transportMode: TransportModeEnumType): this {
        return this.clone(this._state.set('transportMode', transportMode));
    }

    get capacity(): ?number {
        return this._state.get('capacity');
    }

    setCapacity(capacity: ?number): this {
        return this.clone(this._state.set('capacity', capacity));
    }

    get capacities(): Object {
        return this._state.get('capacities');
    }

    setCapacities(capacities: Object): this {
        return this.clone(this._state.set('capacities', capacities));
    }

    get needsRevenueFairnessAcrossDays(): boolean {
        return this._state.get('needsRevenueFairnessAcrossDays');
    }

    setNeedsRevenueFairnessAcrossDays(
        needsRevenueFairnessAcrossDays: boolean
    ): this {
        return this.clone(
            this._state.set(
                'needsRevenueFairnessAcrossDays',
                needsRevenueFairnessAcrossDays
            )
        );
    }

    get autoAssign(): boolean {
        return this._state.get('autoAssign');
    }

    setAutoAssign(autoAssign: boolean): this {
        return this.clone(this._state.set('autoAssign', autoAssign));
    }

    get serializedSchedulingConfig(): string {
        return this._state.get('serializedSchedulingConfig');
    }

    setSerializedSchedulingConfig(serializedSchedulingConfig: string): this {
        return this.clone(
            this._state.set(
                'serializedSchedulingConfig',
                serializedSchedulingConfig
            )
        );
    }

    get serializedVaeConfig(): string {
        return this._state.get('serializedVaeConfig');
    }

    setSerializedVaeConfig(serializedVaeConfig: string): this {
        return this.clone(
            this._state.set('serializedVaeConfig', serializedVaeConfig)
        );
    }

    get needsExistingBeatPlan(): boolean {
        return this._state.get('needsExistingBeatPlan');
    }

    setNeedsExistingBeatPlan(needsExistingBeatPlan: boolean): this {
        return this.clone(
            this._state.set('needsExistingBeatPlan', needsExistingBeatPlan)
        );
    }

    get distributionPlanConfiguration(): PJDistributionPlanConfiguration {
        return this._state.get('distributionPlanConfiguration');
    }

    setDistributionPlanConfiguration(
        distributionPlanConfiguration: PJDistributionPlanConfiguration
    ): this {
        return this.clone(
            this._state.set(
                'distributionPlanConfiguration',
                distributionPlanConfiguration
            )
        );
    }

    get isExtraOutletAllowedInFrozenBeats(): boolean {
        return this._state.get('isExtraOutletAllowedInFrozenBeats');
    }

    setIsExtraOutletAllowedInFrozenBeats(
        isExtraOutletAllowedInFrozenBeats: boolean
    ): this {
        return this.clone(
            this._state.set(
                'isExtraOutletAllowedInFrozenBeats',
                isExtraOutletAllowedInFrozenBeats
            )
        );
    }

    get createDummies(): boolean {
        return this._state.get('createDummies');
    }

    setCreateDummies(createDummies: boolean): this {
        return this.clone(this._state.set('createDummies', createDummies));
    }

    get enableMultiTrip(): boolean {
        return this._state.get('enableMultiTrip');
    }

    setEnableMultiTrip(enableMultiTrip: boolean): this {
        return this.clone(this._state.set('enableMultiTrip', enableMultiTrip));
    }

    get salesmanCategoryGroups(): ?Immutable.Map<string, SalesCategoryGroup> {
        return this._state.get('salesmanCategoryGroups');
    }

    setSalesmanCategoryGroups(
        salesmanCategoryGroups: ?Immutable.Map<string, SalesCategoryGroup>
    ): this {
        return this.clone(
            this._state.set('salesmanCategoryGroups', salesmanCategoryGroups)
        );
    }

    get salesmanCategoryGroupCapacities(): ?Immutable.Map<string, string> {
        return this._state.get('salesmanCategoryGroupCapacities');
    }

    setSalesmanCategoryGroupCapacities(
        salesmanCategoryGroupCapacities: ?Immutable.Map<string, string>
    ): this {
        return this.clone(
            this._state.set(
                'salesmanCategoryGroupCapacities',
                salesmanCategoryGroupCapacities
            )
        );
    }

    get dummySalesmanProperties(): ?Immutable.Map<
        string,
        DummySalesmanProperties
    > {
        return this._state.get('dummySalesmanProperties');
    }

    setDummySalesmanProperties(
        dummySalesmanProperties: ?Immutable.Map<string, DummySalesmanProperties>
    ): this {
        return this.clone(
            this._state.set('dummySalesmanProperties', dummySalesmanProperties)
        );
    }
}
